import {
	type GetAllContactNamesQuery,
	type GetAllContactNamesQueryVariables,
	useGetAllContactNamesLazyQuery,
} from "@repo/server-api";

import { useAsyncSelectCursorPaginatedLazyQuery } from "./AsyncSelectCursorPaginatedLazyQuery";

import type { AsyncSelectSearchHook } from "../BoundAsyncSelect";
import type { IdWithName } from "@repo/shared/common";

export function useAsyncSelectContactQuery(
	types: GetAllContactNamesQueryVariables["types"] = null,
	andTypes: boolean = false,
	showDeleted: boolean = false,
): AsyncSelectSearchHook<IdWithName> {
	const [fetchContacts] = useGetAllContactNamesLazyQuery();

	const buildVariables = (
		search: string,
		cursor: string | null,
	): GetAllContactNamesQueryVariables => {
		return {
			name: search || null,
			types,
			andTypes,
			showDeleted,
			first: 20,
			after: cursor,
		};
	};

	const extractItems = (data: GetAllContactNamesQuery | undefined): IdWithName[] => {
		return (
			data?.allContacts?.nodes?.map((n) => ({
				id: n.id,
				name: n.displayName,
			})) ?? []
		);
	};

	const extractEndCursor = (data: GetAllContactNamesQuery | undefined): string | null =>
		data?.allContacts?.pageInfo?.endCursor ?? null;

	const extractHasNextPage = (data: GetAllContactNamesQuery | undefined): boolean =>
		data?.allContacts?.pageInfo?.hasNextPage ?? false;

	return useAsyncSelectCursorPaginatedLazyQuery<
		GetAllContactNamesQueryVariables,
		GetAllContactNamesQuery,
		IdWithName
	>(fetchContacts, buildVariables, extractItems, extractEndCursor, extractHasNextPage);
}
